import { SearchIcon } from "@heroicons/react/outline";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import posthog from "posthog-js";
import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import SearchBar from "../ui/SearchBar";

import BreadcrumbsPageWrapper from "./BreadcrumbsPageWrapper";
import { DragDropProvider } from "./DragAndDrop/DragDropProvider";
import FilterBar from "./FilterBar";
import { useEntityPreviewUpdates } from "./hooks/useEntityPreviewUpdates";
import useFolderPathBreadcrumbs from "./hooks/useFolderPathBreadcrumbs";
import { useNavigationReset } from "./hooks/useNavigationReset";
import { useRegistryData } from "./hooks/useRegistryData";
import { useSelectionKeyboardShortcuts } from "./hooks/useSelectionKeyboardShortcuts";
import { useViewMode } from "./hooks/useViewMode";
import useNewItemActions from "./NewItemButton/hooks/useNewItemActions";
import RegistryCoreContent from "./RegistryCoreContent";
import { SelectionToolbar } from "./SelectionToolbar";
import { RegistryItem } from "./types";
import { useUnifiedRegistry } from "./unified-registry-context";

import SmartTableCreateDialog from "@/components/SmartTableCreateDialog";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { EntityType, useMoveFolderEntity } from "@/queries/folder-entities";
import { displayToast } from "@/utils/toast";

interface UnifiedRegistryProps {
  currentFolderId: number | null;
  onItemClick?: (item: RegistryItem) => void;
}

const UnifiedRegistry: React.FC<UnifiedRegistryProps> = observer(
  ({ currentFolderId, onItemClick }) => {
    const { workspaceId } = useParams();
    const workspaceIdNum = workspaceId ? parseInt(workspaceId, 10) : null;
    const queryClient = useQueryClient();
    const { viewMode, setViewMode } = useViewMode("grid");
    const authContext = useAuth();

    const hasTrackedRegistry = useRef(false);
    useEffect(() => {
      if (!hasTrackedRegistry.current && currentFolderId === null) {
        posthog.capture("Prompt Registry Viewed", { workspace_id: workspaceIdNum });
        hasTrackedRegistry.current = true;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const registry = useUnifiedRegistry();
    const { folders, allItems, items, isLoading, error, sortState, textResultCount } =
      useRegistryData({
        currentFolderId,
        workspaceId: workspaceIdNum,
      });

    const shouldShowLoading = isLoading;
    const {
      actions,
      isLoading: isCreatingItem,
      smartTableCreateDialogProps,
    } = useNewItemActions(currentFolderId ?? undefined, registry.createInNewTab, workspaceIdNum);

    const showBanner = currentFolderId === null && !registry.modal && !registry.hasActiveFilters;

    useNavigationReset();
    useSelectionKeyboardShortcuts(allItems, registry.whitelist);
    useEntityPreviewUpdates(workspaceIdNum, !registry.modal);

    const breadcrumbProps = useFolderPathBreadcrumbs(currentFolderId);

    const moveMutation = useMoveFolderEntity(authContext?.userToken ?? "");

    const handleMove = async (item: RegistryItem, targetFolderId: number | null) => {
      if (targetFolderId === item.folder_id) {
        return;
      }

      const sourceFolderId = item.folder_id ?? null;

      const isSelected = registry.isSelected(item);

      const entities =
        isSelected && registry.selectedEntities.length > 0 ? registry.selectedEntities : [item];

      try {
        await moveMutation.mutateAsync({
          entities,
          folder_id: targetFolderId,
        });

        const targetFolder = targetFolderId ? folders.find(f => f.id === targetFolderId) : null;
        const destination = targetFolderId === null ? "Home" : targetFolder?.name || "folder";

        const isBulkMove = isSelected && registry.selectedEntities.length > 1;
        const toastMessage = isBulkMove
          ? `${registry.selectedEntities.length} items moved to ${destination}`
          : `${item.name || "Item"} moved to ${destination}`;
        registry.clearSelection();
        displayToast(toastMessage, ToastType.success);

        queryClient.invalidateQueries({
          queryKey: ["folder-entities", workspaceId, sourceFolderId ? sourceFolderId : undefined],
        });

        queryClient.invalidateQueries({
          queryKey: ["folder-entities", workspaceId, null, undefined, undefined, true],
        });

        queryClient.invalidateQueries({
          queryKey: ["folder-entities", workspaceId, targetFolderId ? targetFolderId : undefined],
        });
      } catch {
        // Error handling is done by the mutation's onError callback
      }
    };

    const hasNoResults = folders.length === 0 && items.length === 0 && !isLoading;

    if (error && items.length === 0) {
      return (
        <div className="min-h-screen px-8 py-12">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="mb-4 rounded-full bg-blue-100 p-3">
                <SearchIcon className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="mb-2 text-lg font-medium text-gray-900">Error loading items</h3>
              <p className="text-gray-500">{String(error)}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <DragDropProvider onMove={handleMove}>
        <div className="flex h-full min-h-0 flex-col">
          <BreadcrumbsPageWrapper
            className="flex-shrink-0"
            {...breadcrumbProps}
            onItemClick={
              onItemClick
                ? item => {
                    onItemClick?.({
                      id: item.id!,
                      type: EntityType.FOLDER,
                      name: item.label,
                      path: [],
                    });
                  }
                : undefined
            }
          >
            <SearchBar
              placeholderPhrasesArray={[
                "Search prompts...",
                "Search workflows...",
                "Search datasets...",
                "Search evaluations...",
                "Search A/B tests...",
                "Find your content...",
              ]}
              isLoading={isLoading}
              value={registry.searchQuery ?? ""}
              onChange={value => registry.setSearchQuery(value)}
              className="mb-6"
            />

            <FilterBar
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              currentFolderId={currentFolderId}
              workspaceId={workspaceIdNum}
            />
          </BreadcrumbsPageWrapper>

          <div className="relative flex-1 overflow-hidden" id="registry-core">
            <RegistryCoreContent
              shouldShowLoading={shouldShowLoading}
              hasNoResults={hasNoResults}
              showBanner={showBanner}
              viewMode={viewMode}
              folders={folders}
              items={items}
              allItems={allItems}
              textResultCount={textResultCount}
              isLoading={isLoading}
              currentFolderId={currentFolderId}
              workspaceId={workspaceIdNum}
              sortState={sortState}
              isCreatingItem={isCreatingItem}
              onCreatePrompt={() => actions[EntityType.PROMPT]?.()}
              onItemClick={onItemClick}
            />
          </div>
        </div>
        <SelectionToolbar />
        <SmartTableCreateDialog {...smartTableCreateDialogProps} />
      </DragDropProvider>
    );
  },
);

export default UnifiedRegistry;
